import {action, computed, observable, toJS, makeObservable, set, flow} from 'mobx';
import _ from 'lodash';
import {IMG_BASE_SIZE, CHARCTER_BASE_SIZE, TXT_BASE_SIZE, BASE_WIDTH, BASE_HEIGHT, presetTextStyle, PAGE_TYPE} from '../config';
import {getPageIndexs} from '../utils/digitalAnchor';
import {getFontList, getFontStyleList} from '../services';
import {FONT_FAMILY_LIST} from '../constants/txtFonts';


/* eslint-disable max-nested-callbacks */

export const computeStyle = (element, index = 1) => {
    const {type, ...ext} = element;
    let BASE_INFO = type === 'character' ? CHARCTER_BASE_SIZE : IMG_BASE_SIZE;
    if (type === 'txt') {
        BASE_INFO = TXT_BASE_SIZE;
    }
    const {x, y, w, h, r, clipPath, canvasLeft, canvasTop, scaleX, scaleY} = {...BASE_INFO, ...ext};
    const width = w ? (w * BASE_WIDTH).toFixed(3) : 0;
    const height = h ? (h * BASE_HEIGHT).toFixed(3) : 0;
    const tWidth = (x * BASE_WIDTH).toFixed(3);
    const tHeight = (y * BASE_HEIGHT).toFixed(3);
    const res = {
        width: width ? +width : 'auto',
        height: height ? +height : 'auto',
        top: +tHeight,
        left: +tWidth,
        angle: r ? +r : 0,
        scaleX: scaleX,
        scaleY: scaleY,
        canvasLeft: canvasLeft,
        canvasTop: canvasTop
    };

    if (type === 'image') {
        res.clipPath = clipPath ? clipPath : 'inset(0% 0% 0% 0%)';
    }

    return res;
};

// 画布编辑器核心store
export class EditorStore {
    constructor() {
        makeObservable(this, {
            activeGoods: observable,
            activePageIndex: observable,
            submitParams: observable,
            magicSubmitParams: observable,
            historyStack: observable,
            tempStack: observable,
            txtCanvasMap: observable,
            musicConfig: observable,
            videoBgCount: observable,
            videoBgList: observable,
            videoPasterCount: observable,
            videoPasterMap: observable,
            targets: observable,
            targetObject: observable,
            targetClipVideoObject: observable,
            activityTabKey: observable,
            imageClip: observable,
            videoClip: observable,
            imageClipArguments: observable,
            imgInfoMap: observable,
            riskCheckResult: observable,
            fabricEditor: observable,
            fabricIsLoading: observable,
            fabricIsRebuildGroup: observable,
            scriptId: observable,

            fonts: observable,
            fontsStyles: observable,

            activityTarget: computed,
            activityTargetType: computed,
            isHistoryEmpty: computed,
            isTempEmpty: computed,
            pageId: computed,
            pageType: computed,
            elementList: computed,
            fabricList: computed,
            curPageHasVideoBg: computed,
            curPageVideoPasterCount: computed,
            curPagePptPasterCount: computed,
            isSingleSelect: computed,
            isMutiSelect: computed,
            targetsTypeMap: computed,
            isImageClipping: computed,
            goodsListString: computed,
            curActiveFabricItem: computed,

            setTargetObject: action,
            setTargetClipVideoObject: action,
            setMusicConfig: action,
            setActiveGoods: action,
            setActivePageIndex: action,
            setElementList: action,
            setSubmitParams: action,
            addHistoryStack: action,
            updateParams: action,
            handleBackOption: action,
            handleRevertOption: action,
            clearStack: action,
            cleanTempStack: action,
            setTxtCanvasMap: action,
            setTargets: action,
            setActivityTabKey: action,
            setImageClip: action,
            setVideoClip: action,
            setImageClipArguments: action,
            setImgInfoMap: action,
            cleanImgInfoMap: action,
            setRiskCheckResult: action,
            riskIdsByPageIndex: computed,
            riskElements: computed,
            getFonts: action,
            getFontsStyle: action,
            replaceElement: action,
            batchUpdateParams: action,
            setScriptId: action
        });
    }

    // 剧本ID
    scriptId = '';

    // 选中的商品id
    activeGoods = '';

    // 选中的分镜id
    activePageIndex = '';

    // 要提交的数据
    submitParams = {};

    // submitParams变更标识
    magicSubmitParams = '';

    // 数字人历史栈
    historyStack = [];

    // 暂存栈 暂存回退操作
    tempStack = [];

    // 记录画布中文本元素内容
    txtCanvasMap = {};

    // 背景音乐配置
    musicConfig = {};

    // 剧本中的视频背景数量 url去重
    videoBgCount = 0;

    videoBgList = [];

    // 剧本中的视频贴图数量 url去重
    videoPasterCount = 0;

    videoPasterMap = {};

    // 当前选中元素列表canvasId
    targets = [];

    // 点击选中的元素对象
    targetObject = {};

    // 正在裁剪时的视频对象
    targetClipVideoObject = {};
    activityTabKey = '1';

    imageClip = false;

    imageClipArguments = {};

    imgInfoMap = {};

    videoClip = false;

    riskCheckResult = [];// 风险检测结果

    fabricEditor = null;

    fabricIsLoading = false;

    fabricIsRebuildGroup = true;

    fonts = [];

    fontsStyles = [];

    get activityTarget() {
        if (this.targets.length === 1) {
            return this.targets[0];
        }
        return '';
    }

    get activityTargetType() {
        const target = this.elementList.find(item => item.canvasId === this.activityTarget);
        if (target?.type === 'video' && this.pageType === PAGE_TYPE.VIDEO) {
            return 'video_page';
        }
        return target?.type || 'bg';
    };

    get isHistoryEmpty() {
        return this.historyStack.length;
    };

    get isTempEmpty() {
        return this.tempStack.length;
    };

    // 编辑页面标识 `${goodsId}_${page_index}`
    get pageId() {
        return (this.activeGoods && this.activePageIndex) ? `${this.activeGoods}_${this.activePageIndex}` : '';
    };

    get pageType() {
        const curGoodsPages = this.submitParams.content?.find(item => item.goods_id === this.activeGoods)?.page_list || [];
        return curGoodsPages.find(item => item.page_index === this.activePageIndex)?.page_type || PAGE_TYPE.DEFAULT;
    }

    // 当前选中的分镜元素列表
    get elementList() {
        const curGoodsPages = this.submitParams.content?.find(item => item.goods_id === this.activeGoods)?.page_list || [];
        const curPageElements = curGoodsPages.find(item => item.page_index === this.activePageIndex)?.elements || [];
        return curPageElements;
    };

    get curPageVideoPasterCount() {
        return this.elementList.filter(item => item.type === 'video').length;
    };

    get curPagePptPasterCount() {
        return this.elementList.filter(item => item.type === 'ppt').length;
    };

    get curPageHasVideoBg() {
        return this.elementList.some(item => item.type === 'bg' && item.bgTab === 'bg_video');
    }

    // 分镜元素列表格式化为拖拽元素
    get fabricList() {
        return this.elementList.filter(item => ['image', 'video', 'txt', 'character', 'ppt'].includes(item.type))
            .map((item, index) => {
                const element = {
                    ...item,
                    style: computeStyle(item, index) // 0预留给人像，贴图从1开始
                };

                if (element.type === 'ppt' && element.pptImage) {
                    element.pptImage = {
                        ...element.pptImage,
                        style: computeStyle(element.pptImage)
                    };
                }

                return element;
            });
    };

    get curActiveFabricItem() {
        return this.fabricList.find(item => item.canvasId === this.activityTarget) || {};
    }

    get isSingleSelect() {
        return this.targets.length === 1;
    }

    get isMutiSelect() {
        return this.targets.length > 1;
    }

    get targetsTypeMap() {
        const hasCharacter = this.elementList.some(item => this.targets.includes(item.canvasId) && item.type === 'character');
        const hasImage = this.elementList.some(item => this.targets.includes(item.canvasId) && item.type === 'image');
        const hasVideo = this.elementList.some(item => this.targets.includes(item.canvasId) && item.type === 'video');
        const hasTxt = this.elementList.some(item => this.targets.includes(item.canvasId) && item.type === 'txt');

        return {
            hasCharacter,
            hasImage,
            hasVideo,
            hasTxt
        };
    }

    get isImageClipping() {
        return this.activityTargetType === 'image' && this.imageClip;
    }

    get isVideoClipping() {
        return this.activityTargetType === 'video_page' && this.videoClip;
    }

    // 商品id拼接的字符串
    get goodsListString() {
        const newSubmitParams = toJS(this.submitParams.content);
        return newSubmitParams?.reduce((value, item) => {
            return value + item.goods_id;
        }, '');
    }

    setScriptId(id) {
        this.scriptId = id;
    }

    setMusicConfig = (params) => {
        this.musicConfig = params;
    };

    setActiveGoods = (id) => {
        this.activeGoods = id;
    };

    setActivePageIndex = (id) => {
        this.activePageIndex = id;
    };

    setElementList = (payload) => {
        // 将商品卡置顶
        const goodsCard = payload.filter(item => item.isGoodsCard);
        const other = payload.filter(item => !item.isGoodsCard);
        payload = [...other, ...goodsCard];

        const newSubmitParams = _.cloneDeep(this.submitParams);
        newSubmitParams.content.forEach(item => {
            if (item.goods_id === this.activeGoods) {
                item.page_list?.forEach(page => {
                    if (page.page_index === this.activePageIndex) {
                        page.elements = payload;
                    }
                });
            }
        });
        this.setSubmitParams(newSubmitParams);
    };

    setSubmitParams = (params) => {
        try {
            const formatParams = _.cloneDeep(params);
            const videoBgList = new Set();
            const videoPasterMap = {};
            formatParams.content.forEach(item => {
                item.page_list?.forEach(page => {
                    page.elements?.forEach(element => {
                        if (!element.canvasId) {
                            element.canvasId = _.uniqueId(`${new Date().getTime()}_canvasId_`);
                        }
                        if (element.type === 'video') {
                            videoPasterMap[element.url] = videoPasterMap[element.url] ? videoPasterMap[element.url] + 1 : 1;
                        }
                        if (element.type === 'bg' && element.bgTab === 'bg_video') {
                            videoBgList.add(element.url);
                        }
                    });
                });
            });
            this.submitParams = formatParams;
            this.addHistoryStack(formatParams);
            this.videoBgCount = videoBgList.size;
            this.videoBgList = [...videoBgList];
            this.videoPasterCount = Object.keys(videoPasterMap).length;
            this.videoPasterMap = videoPasterMap;
            this.magicSubmitParams = _.uniqueId();
        }
        catch (e) {
            console.error(e);
        }
    };

    // 添加历史栈
    addHistoryStack = _.throttle(payload => {
        const action = _.cloneDeep(payload);
        const length = this.historyStack.length;
        const target = length ? this.historyStack[length - 1] : {};
        if (_.isEqual(action, target)) {
            // 没有更新的不存
        }
        else {
            this.historyStack.push(action);
            this.historyStack.slice(-10);
            this.cleanTempStack();
        }
    }, 3000, {
        trailing: true
    });

    // 更新画布元素信息
    updateParams(payload) {
        try {
            const {canvasId, force, ...rest} = payload;
            const keys = Object.keys(rest);
            const newSubmitParams = _.cloneDeep(this.submitParams);
            let shouldUpdate = false;
            newSubmitParams.content.forEach(goods => {
                const {page_list = []} = goods;
                page_list.forEach(page => {
                    const {elements = []} = page;
                    elements.forEach(element => {
                        if (element.canvasId === canvasId) {
                            keys.forEach(key => {
                                if ((!element[key] || force) && payload[key] !== undefined && element[key] !== payload[key]) {
                                    element[key] = payload[key];
                                    shouldUpdate = true;
                                }
                            });
                        }
                    });
                });
            });
            if (shouldUpdate) {
                this.setSubmitParams(newSubmitParams);
            };
        }
        catch (e) {
            // 更新出错了
            console.log(e);
        }
    };

    batchUpdateParams(payload) {
        try {
            const newSubmitParams = _.cloneDeep(this.submitParams);
            let shouldUpdate = false;
            newSubmitParams.content.forEach(goods => {
                const {page_list = []} = goods;
                page_list.forEach(page => {
                    const {elements = []} = page;
                    elements.forEach(element => {
                        if (payload.some(item => item.canvasId === element.canvasId)) {
                            const target = payload.find(item => item.canvasId === element.canvasId);
                            Object.keys({...element, ...target}).forEach(key => {
                                if (target[key]) {
                                    element[key] = target[key];
                                    shouldUpdate = true;
                                }
                            });
                        }
                    });
                });
            });
            if (shouldUpdate) {
                this.setSubmitParams(newSubmitParams);
            };
        }
        catch (e) {
            // 更新出错了
            console.log(e);
        }
    }

    replaceElement(payload) {
        try {
            const {canvasId} = payload;
            const newSubmitParams = _.cloneDeep(this.submitParams);
            let shouldUpdate = false;
            newSubmitParams.content.forEach(goods => {
                const {page_list = []} = goods;
                page_list.forEach(page => {
                    const {elements = []} = page;
                    elements.forEach(element => {
                        if (element.canvasId === canvasId) {
                            Object.keys({...element, ...payload}).forEach(key => {
                                if (payload[key]) {
                                    element[key] = payload[key];
                                }
                                else {
                                    delete element[key];
                                }
                            });
                            element.canvasId = _.uniqueId(`${new Date().getTime()}_canvasId_`);
                            shouldUpdate = true;
                        }
                    });
                });
            });
            if (shouldUpdate) {
                this.setSubmitParams(newSubmitParams);
            };
        }
        catch (e) {
            // 更新出错了
            console.log(e);
        }
    };

    // 回退操作
    handleBackOption() {
        if (this.historyStack.length > 1) {
            const discarded = this.historyStack.pop();
            this.tempStack.push(discarded);
            if (this.historyStack.length) {
                const length = this.historyStack.length;
                this.submitParams = this.historyStack[length - 1];
                this.magicSubmitParams = _.uniqueId();
            }
        }
    }

    // 撤销回退
    handleRevertOption() {
        if (this.tempStack.length) {
            const lastActions = this.tempStack.pop();
            this.historyStack.push(lastActions);
            this.submitParams = lastActions;
            this.magicSubmitParams = _.uniqueId();
        }
    }

    // 清空栈
    clearStack() {
        this.historyStack.splice();
        this.tempStack.splice();
    }

    // 清空暂存栈
    cleanTempStack() {
        this.tempStack.splice();
    }

    setTxtCanvasMap(key, value) {
        this.txtCanvasMap[key] = value;
    }

    setTargets(payload) {
        // 保存选中元素canvasId
        this.targets = payload;
        // 清空裁切状态
        this.imageClip = false;
        this.imageClipArguments = {};
        this.videoClip = false;
    }

    setActivityTabKey(key) {
        this.activityTabKey = key;
    }

    setImageClip(payload) {
        this.imageClip = payload;
    }

    setImageClipArguments(payload) {
        this.imageClipArguments = payload;
    }
    setVideoClip(payload) {
        this.videoClip = payload;
    }

    setImgInfoMap(url, info) {
        set(this.imgInfoMap, url, info);
    }

    cleanImgInfoMap() {
        this.imgInfoMap = {};
    }

    setRiskCheckResult(list) {
        this.riskCheckResult = list;
    }

    get riskIdsByPageIndex() {
        const pageIndexs = getPageIndexs(this.submitParams);
        const {index} = pageIndexs.find(it => it.pageIndex === this.activePageIndex) || {};
        if (this.riskCheckResult.length === 0) {
            return [];
        }
        return this.riskCheckResult?.get(index + '')?.elements?.map(it => it.canvasId) || [];
    };

    get riskElements() {
        const pageIndexs = getPageIndexs(this.submitParams) || [];
        const {index} = pageIndexs.find(it => it.pageIndex === this.activePageIndex) || {};

        if (this.riskCheckResult.length === 0) {
            return [];
        }
        return this.riskCheckResult?.get(index + '')?.elements || [];
    };

    setFabricEditor(payload) {
        this.fabricEditor = payload;
    };

    setTargetObject(object) {
        this.targetObject = object;
    }

    setTargetClipVideoObject(object) {
        this.targetClipVideoObject = object;
    }

    setFabricIsLoading(payload) {
        this.fabricIsLoading = payload;
    };

    setFabricIsRebuildGroup(payload) {
        this.fabricIsRebuildGroup = payload;
    };

    getFonts = flow(function* () {
        if (this.fonts.length) {
            return;
        }

        const res = yield getFontList({
            pn: 1,
            rn: 100
        });

        const fontList = res?.data?.list || [];

        if (fontList.length) {
            // 创建一个新的style元素来保存@font-face规则
            const newStyle = document.createElement('style');

            const genderData = fontList.map(item => {
                const fontFaceRule = `
                    @font-face {
                    font-family: '${item.value}';
                    src: url('${item.path}') format('truetype');
                    font-weight: normal;
                    font-style: normal;
                    }
                `;

                newStyle.appendChild(document.createTextNode(fontFaceRule));

                return {
                    id: 2,
                    label: item.name,
                    value: item.value,
                    imageUrl: item.img,
                    selectedImageUrl: item.img_selected
                };
            });

            // 将style元素添加到文档的head部分
            document.head.appendChild(newStyle);

            this.fonts = genderData;
        }
        else {
            this.fonts = FONT_FAMILY_LIST;
        }
    });

    getFontsStyle = flow(function* () {
        if (this.fontsStyles.length) {
            return;
        }

        const res = yield getFontStyleList({
            pn: 1,
            rn: 100
        });

        const fontStyleList = res?.data?.list || [];

        if (fontStyleList.length) {
            this.fontsStyles = fontStyleList.map(item => {
                return JSON.parse(item.value);
            });
        }
        else {
            this.fontsStyles = presetTextStyle;
        }
    });
};

